import { Box, BoxProps, CircularProgress, Stack } from "@mui/material";

import React, { useState } from "react";
import { useDropzone } from "react-dropzone";

import { DropzoneEmptyState } from "./components/DropzoneEmptyState";
import { SelectedFileView } from "./components/SelectedFileView";

export type DropFileProps = BoxProps & {
  onFileUpload?: (files: File[]) => Promise<void>;
  disabled?: boolean;
  selectedFile?: File;
  acceptedFormats?: string;
};

// MIME types for certificate/key extensions (used by CertificateMethod)
const CERT_MIME_TYPES = ["application/x-x509-ca-cert", "application/pkix-cert", "application/x-pem-file"] as const;

const KEY_MIME_TYPES = ["application/x-pem-file", "application/pkcs8", "application/x-pkcs8"] as const;

// Extension → MIME types for react-dropzone. Add more entries here when new formats are needed.

const EXTENSION_TO_MIME: Record<string, string[]> = {
  ".xml": ["application/xml", "text/xml"],
  ".crt": [...CERT_MIME_TYPES],
  ".pem": [...CERT_MIME_TYPES, ...KEY_MIME_TYPES], // PEM can contain both
  ".cer": [...CERT_MIME_TYPES],
  ".key": [...KEY_MIME_TYPES],
} as const;

// Convert accept string (e.g. ".xml" or ".crt,.pem") to react-dropzone format
const getAcceptObject = (acceptString?: string): Record<string, string[]> | undefined => {
  if (!acceptString) return undefined;
  const extensions = acceptString
    .split(",")
    .map((ext) => ext.trim().replace(/^\./, ""))
    .filter(Boolean)
    .map((ext) => `.${ext}`);
  if (extensions.length === 0) return undefined;

  const acceptObj: Record<string, string[]> = {};

  extensions.forEach((ext) => {
    const lower = ext.toLowerCase();
    const mimeTypes = EXTENSION_TO_MIME[lower];
    if (mimeTypes) {
      mimeTypes.forEach((mime) => {
        if (!acceptObj[mime]) acceptObj[mime] = [];
        if (!acceptObj[mime].includes(lower)) acceptObj[mime].push(lower);
      });
    }
  });

  return Object.keys(acceptObj).length > 0 ? acceptObj : undefined;
};

export const DropSignedFile: React.FC<DropFileProps> = ({
  children,
  onFileUpload = () => Promise.resolve(),
  disabled = false,
  selectedFile,
  acceptedFormats,
  ...boxProps
}) => {
  const [callFn, setCallFn] = useState(false);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: async (files: File[]) => {
      if (disabled) return;
      setCallFn(true);
      await onFileUpload(files);
      setCallFn(false);
    },
    multiple: true,
    noClick: true,
    disabled: disabled,
    accept: getAcceptObject(acceptedFormats),
  });

  return (
    <Box
      {...boxProps}
      {...getRootProps()}
      style={{
        position: "relative",
        marginTop: "16px",
        border: disabled ? "1px dashed #CCCCCC" : isDragActive ? "1px dashed #0052a5" : "1px dashed #9EA5AB",
        background: disabled ? "#F5F5F5" : isDragActive ? "#D8E6F3" : "transparent",
        borderRadius: "8px",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <Stack spacing={1} height="128px" width="100%" direction="row" justifyContent="center" alignItems="center">
        {callFn && (
          <Stack
            position="absolute"
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
            style={{
              background: "#D8E6F3",
              borderRadius: "8px",
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: "#FFF" }} />
          </Stack>
        )}

        {selectedFile ? (
          <SelectedFileView
            selectedFile={selectedFile}
            disabled={disabled}
            acceptedFormats={acceptedFormats}
            onFileUpload={onFileUpload}
            setCallFn={setCallFn}
          />
        ) : (
          <DropzoneEmptyState
            isDragActive={isDragActive}
            disabled={disabled}
            acceptedFormats={acceptedFormats}
            onFileUpload={onFileUpload}
            setCallFn={setCallFn}
          />
        )}
      </Stack>
    </Box>
  );
};
